import React, { useState, useMemo } from 'react';
import { Plus, ChevronLeft, Trash2, TrendingUp, BarChart2, ArrowRight, Download, Share2, Copy, Check, ExternalLink } from 'lucide-react';
import { Card, SectionHeader, Btn, Badge, Modal, Field, Input, EmptyState } from '../components/UI';
import { fmtAmt } from '../utils/helpers';
import { CLIENT_AD_TYPES } from '../utils/storage';
import { supabase } from '../utils/supabaseClient';
import { fetchMetaInsights, summarize } from '../utils/meta';
import { metaClientForId } from '../utils/metaClients';

// Map a Meta insights response onto the performance-report data shape.
function metaToPerfData(res, adType) {
  const t = res.accountMetrics || summarize(res.campaigns);
  const isService = adType === 'Service Sector';
  const campaigns = (res.campaigns || []).filter(c => c.metrics).map(c => {
    const m = c.metrics;
    return {
      name: c.name, objective: 'Sales (ROAS)',
      spent: Math.round(m.spend), roas: m.roas ? m.roas.toFixed(2) : '',
      cpl: m.cpl ? Math.round(m.cpl) : '', cpv: '', cpg: '',
      impressions: m.impressions, clicks: m.clicks,
      ctr: (m.ctr || 0).toFixed(2), cpm: Math.round(m.cpm),
      convRate: '', notes: '',
    };
  });
  const best = campaigns.slice().sort((a, b) => (parseFloat(b.roas) || 0) - (parseFloat(a.roas) || 0))[0];
  return {
    dateRange: res.window?.since ? `${res.window.since} – ${res.window.until}` : '',
    totalSpent: Math.round(t.spend),
    totalSalesBefore: isService ? '' : Math.round(t.value || 0),
    netSales: isService ? '' : Math.round(t.value || 0),
    overallCPL: isService && t.cpl ? Math.round(t.cpl) : '',
    campaigns,
    topAd: { description: best ? `Top campaign: ${best.name} (${best.roas}x ROAS)` : '', comments: '', links: '' },
    suggestions: '',
  };
}
function monthRange(ym) {
  if (!ym) return {};
  const [y, m] = ym.split('-').map(Number);
  const since = `${ym}-01`;
  const until = `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
  return { since, until };
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CAMPAIGN_OBJECTIVES = ['Sales (ROAS)','Traffic (Cost Per Visit)','Leads (CPL)','Engagement','Awareness','App Installs'];

const sel = { width:'100%',padding:'9px 12px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box' };
const inp = { width:'100%',padding:'9px 12px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box' };
const ta  = { ...inp, resize:'vertical', fontFamily:'var(--font)', lineHeight:1.5 };

function Label({ children }) {
  return <div style={{fontSize:11,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:6}}>{children}</div>;
}
function Section({ title, children }) {
  return (
    <div style={{marginBottom:24}}>
      <div style={{fontSize:13,fontFamily:'var(--serif)',fontWeight:400,color:'var(--text)',marginBottom:12,paddingBottom:8,borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:8}}>
        {title}
      </div>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>{children}</div>;
}
function FieldWrap({ label, children }) {
  return <div><Label>{label}</Label>{children}</div>;
}

// ─── Clients eligible for each report type ────────────────────────
function getEligibleClients(clients, reportType) {
  const result = [];
  clients.forEach(c => {
    // Guard: treat missing fields as false/null so old localStorage data still shows clients
    const hasSocial = c.socialMedia === true;
    const hasAds    = !!c.adType;
    if (reportType === 'social' && hasSocial)       result.push({ id: c.id, name: c.name });
    if (reportType === 'performance' && hasAds)     result.push({ id: c.id, name: c.name, adType: c.adType });
    if (c.subAccounts) {
      c.subAccounts.forEach(sub => {
        if (reportType === 'social' && sub.socialMedia)    result.push({ id: sub.id, name: sub.name, parentName: c.name });
        if (reportType === 'performance' && sub.adType)    result.push({ id: sub.id, name: sub.name, adType: sub.adType, parentName: c.name });
      });
    }
  });
  return result;
}

// Fallback: if no eligible clients found (old data without adType/socialMedia),
// return all clients for selection so the user isn't stuck
function getEligibleClientsWithFallback(clients, reportType) {
  const eligible = getEligibleClients(clients, reportType);
  if (eligible.length > 0) return eligible;
  // Old data — return everything
  return clients.map(c => ({ id: c.id, name: c.name, adType: c.adType || 'E-Commerce', _fallback: true }));
}

function getClientAdType(clients, clientId) {
  for (const c of clients) {
    if (c.id === clientId) return c.adType;
    if (c.subAccounts) {
      const sub = c.subAccounts.find(s => s.id === clientId);
      if (sub) return sub.adType;
    }
  }
  return null;
}

function nowMonthYear() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}

function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MONTHS[parseInt(m)-1]} ${y}`;
}

// ─── Empty performance report data ───────────────────────────────
function emptyPerfData() {
  return {
    dateRange: '', totalSpent: '', totalSalesBefore: '', netSales: '', overallCPL: '',
    campaigns: [],
    topAd: { description: '', comments: '', links: '' },
    suggestions: '',
  };
}

// ─── Empty social report data ─────────────────────────────────────
function emptySocialData() {
  return {
    viewsFB: '', viewsIG: '',
    reachFB: '', reachIG: '',
    interactionsFB: '', interactionsIG: '',
    followsFB: '', followsIG: '',
    topPostsFB: ['','',''], topPostsIG: ['','',''],
    topStoriesFB: ['','',''], topStoriesIG: ['','',''],
    publishedFBPosts: '', publishedFBStories: '', publishedIGPosts: '', publishedIGStories: '',
    msgConvosFB: '', msgConvosIG: '',
    suggestions: '',
  };
}

function emptyCampaign() {
  return { name:'', objective:'Sales (ROAS)', spent:'', cpg:'', roas:'', cpv:'', cpl:'', impressions:'', clicks:'', ctr:'', cpm:'', convRate:'', notes:'' };
}

// ─── Performance report form ──────────────────────────────────────
function PerfReportForm({ data, setData, adType }) {
  const isEcom    = adType && adType.includes('E-Commerce');
  const isDMs     = adType === 'E-Commerce & DMs';
  const isService = adType === 'Service Sector';

  function setField(k, v) { setData(p => ({ ...p, [k]: v })); }
  function setCampaign(i, k, v) {
    setData(p => {
      const camps = [...p.campaigns];
      camps[i] = { ...camps[i], [k]: v };
      return { ...p, campaigns: camps };
    });
  }
  function addCampaign() { setData(p => ({ ...p, campaigns: [...p.campaigns, emptyCampaign()] })); }
  function removeCampaign(i) { setData(p => ({ ...p, campaigns: p.campaigns.filter((_,idx)=>idx!==i) })); }

  return (
    <div>
      <Section title="📅 Overview">
        <Row>
          <FieldWrap label="Date Range"><input style={inp} value={data.dateRange} onChange={e=>setField('dateRange',e.target.value)} placeholder="e.g. 1 Apr – 30 Apr 2026"/></FieldWrap>
          <FieldWrap label="Total Amount Spent (PKR)"><input style={inp} type="number" value={data.totalSpent} onChange={e=>setField('totalSpent',e.target.value)} placeholder="e.g. 250000"/></FieldWrap>
        </Row>
        {isEcom && (
          <Row>
            <FieldWrap label="Total Sales – Before Returns (PKR)"><input style={inp} type="number" value={data.totalSalesBefore} onChange={e=>setField('totalSalesBefore',e.target.value)}/></FieldWrap>
            <FieldWrap label="Net Sales (PKR)"><input style={inp} type="number" value={data.netSales} onChange={e=>setField('netSales',e.target.value)}/></FieldWrap>
          </Row>
        )}
        {(isService || isDMs) && (
          <FieldWrap label="Overall Cost Per Lead (CPL) – PKR">
            <input style={{...inp,maxWidth:300}} type="number" value={data.overallCPL} onChange={e=>setField('overallCPL',e.target.value)}/>
          </FieldWrap>
        )}
      </Section>

      <Section title="📊 Campaign Breakdown">
        {data.campaigns.length === 0 && (
          <div style={{color:'var(--text3)',fontSize:13,marginBottom:12}}>No campaigns added yet.</div>
        )}
        {data.campaigns.map((camp, i) => {
          const showROAS = camp.objective === 'Sales (ROAS)';
          const showCPV  = camp.objective === 'Traffic (Cost Per Visit)';
          const showCPL  = camp.objective === 'Leads (CPL)';
          return (
            <div key={i} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'1rem',marginBottom:12,border:'1px solid var(--border)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                <span style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em'}}>Campaign {i+1}</span>
                <button onClick={()=>removeCampaign(i)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',display:'flex',padding:2}}><Trash2 size={13}/></button>
              </div>
              <Row>
                <FieldWrap label="Campaign Name"><input style={inp} value={camp.name} onChange={e=>setCampaign(i,'name',e.target.value)} placeholder="e.g. Ramadan Sale"/></FieldWrap>
                <FieldWrap label="Objective">
                  <select style={sel} value={camp.objective} onChange={e=>setCampaign(i,'objective',e.target.value)}>
                    {CAMPAIGN_OBJECTIVES.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </FieldWrap>
              </Row>
              <Row>
                <FieldWrap label="Amount Spent (PKR)"><input style={inp} type="number" value={camp.spent} onChange={e=>setCampaign(i,'spent',e.target.value)}/></FieldWrap>
                <FieldWrap label="Cost Per Goal (CPG) – PKR"><input style={inp} type="number" value={camp.cpg} onChange={e=>setCampaign(i,'cpg',e.target.value)}/></FieldWrap>
              </Row>
              <Row>
                <FieldWrap label="Impressions"><input style={inp} type="number" value={camp.impressions} onChange={e=>setCampaign(i,'impressions',e.target.value)}/></FieldWrap>
                <FieldWrap label="Clicks"><input style={inp} type="number" value={camp.clicks} onChange={e=>setCampaign(i,'clicks',e.target.value)}/></FieldWrap>
              </Row>
              <Row>
                <FieldWrap label="CTR (%)"><input style={inp} type="number" value={camp.ctr} onChange={e=>setCampaign(i,'ctr',e.target.value)} placeholder="e.g. 2.4"/></FieldWrap>
                <FieldWrap label="CPM (PKR)"><input style={inp} type="number" value={camp.cpm} onChange={e=>setCampaign(i,'cpm',e.target.value)}/></FieldWrap>
              </Row>
              {showROAS && (
                <Row>
                  <FieldWrap label="ROAS"><input style={inp} type="number" step="0.01" value={camp.roas} onChange={e=>setCampaign(i,'roas',e.target.value)} placeholder="e.g. 4.2"/></FieldWrap>
                  <FieldWrap label="Conversion Rate (%)"><input style={inp} type="number" value={camp.convRate} onChange={e=>setCampaign(i,'convRate',e.target.value)}/></FieldWrap>
                </Row>
              )}
              {showCPV && (
                <FieldWrap label="Cost Per Visit (PKR)"><input style={{...inp,maxWidth:300}} type="number" value={camp.cpv} onChange={e=>setCampaign(i,'cpv',e.target.value)}/></FieldWrap>
              )}
              {showCPL && (
                <FieldWrap label="Cost Per Lead (PKR)"><input style={{...inp,maxWidth:300}} type="number" value={camp.cpl} onChange={e=>setCampaign(i,'cpl',e.target.value)}/></FieldWrap>
              )}
              <FieldWrap label="Campaign Notes">
                <textarea style={{...ta,marginTop:4}} rows={2} value={camp.notes} onChange={e=>setCampaign(i,'notes',e.target.value)} placeholder="Any notes on this campaign…"/>
              </FieldWrap>
            </div>
          );
        })}
        <Btn size="sm" variant="ghost" onClick={addCampaign}><Plus size={12}/>Add campaign</Btn>
      </Section>

      <Section title="🏆 Top Performing Ad">
        <FieldWrap label="Description / Ad details">
          <textarea style={ta} rows={3} value={data.topAd?.description||''} onChange={e=>setData(p=>({...p,topAd:{...p.topAd,description:e.target.value}}))} placeholder="Describe the top performing ad creative, audience, placement…"/>
        </FieldWrap>
        <div style={{marginTop:12}}>
          <FieldWrap label="Ad Content Links (one per line)">
            <textarea style={ta} rows={3} value={data.topAd?.links||''} onChange={e=>setData(p=>({...p,topAd:{...p.topAd,links:e.target.value}}))} placeholder="https://…"/>
          </FieldWrap>
        </div>
        <div style={{marginTop:12}}>
          <FieldWrap label="Comments & Next Steps">
            <textarea style={ta} rows={3} value={data.topAd?.comments||''} onChange={e=>setData(p=>({...p,topAd:{...p.topAd,comments:e.target.value}}))} placeholder="What do we plan to do with this insight?"/>
          </FieldWrap>
        </div>
      </Section>

      <Section title="💡 Suggestions for Next Month">
        <textarea style={ta} rows={4} value={data.suggestions} onChange={e=>setField('suggestions',e.target.value)} placeholder="Recommendations, budget changes, creative direction, targeting adjustments…"/>
      </Section>
    </div>
  );
}

// ─── Social report form ───────────────────────────────────────────
function SocialReportForm({ data, setData }) {
  function setField(k, v) { setData(p => ({ ...p, [k]: v })); }
  function setTopPost(platform, i, v) {
    setData(p => {
      const arr = [...(p[`topPosts${platform}`]||['','',''])];
      arr[i] = v;
      return { ...p, [`topPosts${platform}`]: arr };
    });
  }
  function setTopStory(platform, i, v) {
    setData(p => {
      const arr = [...(p[`topStories${platform}`]||['','',''])];
      arr[i] = v;
      return { ...p, [`topStories${platform}`]: arr };
    });
  }

  const metricRow = (labelFB, labelIG, keyFB, keyIG, placeholder='') => (
    <Row>
      <FieldWrap label={labelFB}><input style={inp} type="number" value={data[keyFB]||''} onChange={e=>setField(keyFB,e.target.value)} placeholder={placeholder}/></FieldWrap>
      <FieldWrap label={labelIG}><input style={inp} type="number" value={data[keyIG]||''} onChange={e=>setField(keyIG,e.target.value)} placeholder={placeholder}/></FieldWrap>
    </Row>
  );

  return (
    <div>
      <Section title="👁️ Views">
        {metricRow('Facebook Views','Instagram Views','viewsFB','viewsIG')}
      </Section>
      <Section title="📡 Reach">
        {metricRow('Facebook Reach','Instagram Reach','reachFB','reachIG')}
      </Section>
      <Section title="💬 Content Interactions">
        {metricRow('Facebook Interactions','Instagram Interactions','interactionsFB','interactionsIG')}
      </Section>
      <Section title="➕ Follows">
        {metricRow('Facebook Follows','Instagram Follows','followsFB','followsIG')}
      </Section>

      <Section title="🏅 Top Posts / Reels by Views (Top 3)">
        <div style={{marginBottom:12}}>
          <div style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:8}}>Facebook</div>
          {[0,1,2].map(i=><div key={i} style={{marginBottom:8}}><FieldWrap label={`#${i+1} Post/Reel`}><input style={inp} value={(data.topPostsFB||[])[i]||''} onChange={e=>setTopPost('FB',i,e.target.value)} placeholder="Post title or link"/></FieldWrap></div>)}
        </div>
        <div>
          <div style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:8}}>Instagram</div>
          {[0,1,2].map(i=><div key={i} style={{marginBottom:8}}><FieldWrap label={`#${i+1} Post/Reel`}><input style={inp} value={(data.topPostsIG||[])[i]||''} onChange={e=>setTopPost('IG',i,e.target.value)} placeholder="Post title or link"/></FieldWrap></div>)}
        </div>
      </Section>

      <Section title="📖 Top Stories by Views (Top 3)">
        <div style={{marginBottom:12}}>
          <div style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:8}}>Facebook</div>
          {[0,1,2].map(i=><div key={i} style={{marginBottom:8}}><FieldWrap label={`#${i+1} Story`}><input style={inp} value={(data.topStoriesFB||[])[i]||''} onChange={e=>setTopStory('FB',i,e.target.value)} placeholder="Story description or link"/></FieldWrap></div>)}
        </div>
        <div>
          <div style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:8}}>Instagram</div>
          {[0,1,2].map(i=><div key={i} style={{marginBottom:8}}><FieldWrap label={`#${i+1} Story`}><input style={inp} value={(data.topStoriesIG||[])[i]||''} onChange={e=>setTopStory('IG',i,e.target.value)} placeholder="Story description or link"/></FieldWrap></div>)}
        </div>
      </Section>

      <Section title="📅 Published Content">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <div>
            <div style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:8}}>Facebook</div>
            <Row>
              <FieldWrap label="Total Posts"><input style={inp} type="number" value={data.publishedFBPosts||''} onChange={e=>setField('publishedFBPosts',e.target.value)}/></FieldWrap>
              <FieldWrap label="Total Stories"><input style={inp} type="number" value={data.publishedFBStories||''} onChange={e=>setField('publishedFBStories',e.target.value)}/></FieldWrap>
            </Row>
          </div>
          <div>
            <div style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',marginBottom:8}}>Instagram</div>
            <Row>
              <FieldWrap label="Total Posts"><input style={inp} type="number" value={data.publishedIGPosts||''} onChange={e=>setField('publishedIGPosts',e.target.value)}/></FieldWrap>
              <FieldWrap label="Total Stories"><input style={inp} type="number" value={data.publishedIGStories||''} onChange={e=>setField('publishedIGStories',e.target.value)}/></FieldWrap>
            </Row>
          </div>
        </div>
      </Section>

      <Section title="📩 Messaging Conversations Started">
        {metricRow('Facebook','Instagram','msgConvosFB','msgConvosIG')}
      </Section>

      <Section title="💡 Suggestions for Next Month">
        <textarea style={ta} rows={4} value={data.suggestions||''} onChange={e=>setField('suggestions',e.target.value)} placeholder="Content themes, posting frequency, format recommendations…"/>
      </Section>
    </div>
  );
}

// ─── Month-on-month comparison table ────────────────────────────
function PerfComparison({ reports }) {
  const [compareCount, setCompareCount] = useState(2);
  const sorted = [...reports].sort((a,b)=>b.monthYear.localeCompare(a.monthYear));
  const slice  = sorted.slice(0, compareCount);

  if (reports.length < 2) return null;

  const maxMonths = Math.min(reports.length, 12);
  const options = Array.from({length:maxMonths-1},(_,i)=>i+2);

  const metricRow = (label, getter, fmt=(v)=>v||'—') => (
    <tr style={{borderBottom:'1px solid var(--border)'}}>
      <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)',fontWeight:500,whiteSpace:'nowrap'}}>{label}</td>
      {slice.map((r,i)=>{
        const val = getter(r.data);
        const prev = slice[i+1] ? getter(slice[i+1].data) : null;
        const num  = parseFloat(val);
        const prevNum = parseFloat(prev);
        const up   = prev && !isNaN(num) && !isNaN(prevNum) && num > prevNum;
        const down = prev && !isNaN(num) && !isNaN(prevNum) && num < prevNum;
        return (
          <td key={i} style={{padding:'8px 12px',fontSize:12,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',color:up?'var(--green)':down?'var(--red)':'var(--text2)',textAlign:'right'}}>
            {fmt(val)}{up&&' ↑'}{down&&' ↓'}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div style={{marginTop:24}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <span style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:14,color:'var(--text)'}}>Month-on-month comparison</span>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--text3)'}}>
          Show last
          <select value={compareCount} onChange={e=>setCompareCount(Number(e.target.value))} style={{...sel,width:'auto',padding:'4px 8px',fontSize:12}}>
            {options.map(n=><option key={n} value={n}>{n} months</option>)}
          </select>
          months
        </div>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'var(--bg3)'}}>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',borderBottom:'1px solid var(--border)'}}>Metric</th>
              {slice.map((r,i)=>(
                <th key={i} style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>
                  {monthLabel(r.monthYear)}{i===0&&<span style={{color:'var(--accent)',marginLeft:4}}>●</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metricRow('Total Spent (PKR)', d=>d.totalSpent, v=>v?fmtAmt(Number(v)):'—')}
            {metricRow('Total Sales Before Returns', d=>d.totalSalesBefore, v=>v?fmtAmt(Number(v)):'—')}
            {metricRow('Net Sales', d=>d.netSales, v=>v?fmtAmt(Number(v)):'—')}
            {metricRow('Overall CPL (PKR)', d=>d.overallCPL, v=>v?fmtAmt(Number(v)):'—')}
            {metricRow('Campaigns', d=>d.campaigns?.length)}
            {metricRow('Total Impressions', d=>{
              const total = d.campaigns?.reduce((s,c)=>s+(parseFloat(c.impressions)||0),0)||0;
              return total||'';
            }, v=>v?(Number(v)).toLocaleString():'—')}
            {metricRow('Total Clicks', d=>{
              const total = d.campaigns?.reduce((s,c)=>s+(parseFloat(c.clicks)||0),0)||0;
              return total||'';
            }, v=>v?(Number(v)).toLocaleString():'—')}
            {metricRow('Avg. ROAS', d=>{
              const roasCamps = d.campaigns?.filter(c=>c.roas&&c.objective?.includes('ROAS'))||[];
              if (!roasCamps.length) return '';
              return (roasCamps.reduce((s,c)=>s+parseFloat(c.roas||0),0)/roasCamps.length).toFixed(2);
            })}
          </tbody>
        </table>
      </div>
      <p style={{fontSize:11,color:'var(--text3)',marginTop:6}}>↑ = improved vs previous month · ↓ = declined · current month marked ●</p>
    </div>
  );
}

function SocialComparison({ reports }) {
  const [compareCount, setCompareCount] = useState(2);
  const sorted = [...reports].sort((a,b)=>b.monthYear.localeCompare(a.monthYear));
  const slice  = sorted.slice(0, compareCount);
  if (reports.length < 2) return null;
  const maxMonths = Math.min(reports.length, 12);
  const options = Array.from({length:maxMonths-1},(_,i)=>i+2);

  const metricRow = (label, getter) => (
    <tr style={{borderBottom:'1px solid var(--border)'}}>
      <td style={{padding:'8px 12px',fontSize:12,color:'var(--text3)',fontWeight:500}}>{label}</td>
      {slice.map((r,i)=>{
        const val = parseFloat(getter(r.data));
        const prev = slice[i+1] ? parseFloat(getter(slice[i+1].data)) : null;
        const up   = prev != null && !isNaN(val) && !isNaN(prev) && val > prev;
        const down = prev != null && !isNaN(val) && !isNaN(prev) && val < prev;
        return (
          <td key={i} style={{padding:'8px 12px',fontSize:12,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',color:up?'var(--green)':down?'var(--red)':'var(--text2)',textAlign:'right'}}>
            {isNaN(val)?'—':val.toLocaleString()}{up&&' ↑'}{down&&' ↓'}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div style={{marginTop:24}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <span style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:14,color:'var(--text)'}}>Month-on-month comparison</span>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--text3)'}}>
          Show last
          <select value={compareCount} onChange={e=>setCompareCount(Number(e.target.value))} style={{...sel,width:'auto',padding:'4px 8px',fontSize:12}}>
            {options.map(n=><option key={n} value={n}>{n} months</option>)}
          </select>
          months
        </div>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'var(--bg3)'}}>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',borderBottom:'1px solid var(--border)'}}>Metric</th>
              {slice.map((r,i)=>(
                <th key={i} style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.14em',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>
                  {monthLabel(r.monthYear)}{i===0&&<span style={{color:'var(--accent)',marginLeft:4}}>●</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metricRow('Total Views (FB+IG)', d=>(parseFloat(d.viewsFB||0)+parseFloat(d.viewsIG||0))||'')}
            {metricRow('Total Reach (FB+IG)', d=>(parseFloat(d.reachFB||0)+parseFloat(d.reachIG||0))||'')}
            {metricRow('Total Interactions', d=>(parseFloat(d.interactionsFB||0)+parseFloat(d.interactionsIG||0))||'')}
            {metricRow('Total Follows', d=>(parseFloat(d.followsFB||0)+parseFloat(d.followsIG||0))||'')}
            {metricRow('FB Posts Published', d=>d.publishedFBPosts||'')}
            {metricRow('IG Posts Published', d=>d.publishedIGPosts||'')}
            {metricRow('Messaging Convos', d=>(parseFloat(d.msgConvosFB||0)+parseFloat(d.msgConvosIG||0))||'')}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main ReportsPage ─────────────────────────────────────────────
export default function ReportsPage({ store, persist, showToast, canSeeSocial = true, canSeePerfMarketing = true }) {
  const reports = store.reports || [];
  const clients = store.clients || [];

  const [view,         setView]         = useState('list'); // 'list'|'new'|'detail'
  const [reportType,   setReportType]   = useState(null);   // 'social'|'performance'
  const [selectedId,   setSelectedId]   = useState(null);
  const [form,         setForm]         = useState({ clientId:'', monthYear:nowMonthYear() });
  const [perfData,     setPerfData]     = useState(emptyPerfData);
  const [metaBusy,     setMetaBusy]     = useState(false);
  const [socialData,   setSocialData]   = useState(emptySocialData);
  const [filterType,   setFilterType]   = useState('all');  // 'all'|'social'|'performance'
  const [filterClient, setFilterClient] = useState('');

  const eligiblePerf   = useMemo(()=>getEligibleClientsWithFallback(clients,'performance'), [clients]);
  const eligibleSocial = useMemo(()=>getEligibleClientsWithFallback(clients,'social'), [clients]);

  // Filtered report list
  const filtered = reports.filter(r => {
    if (filterType !== 'all' && r.type !== filterType) return false;
    if (filterClient && r.clientId !== filterClient) return false;
    if (!canSeeSocial && r.type === 'social') return false;
    if (!canSeePerfMarketing && r.type === 'performance') return false;
    return true;
  }).sort((a,b)=>b.monthYear.localeCompare(a.monthYear));

  function getClientName(clientId) {
    for (const c of clients) {
      if (c.id === clientId) return c.name;
      if (c.subAccounts) { const s = c.subAccounts.find(x=>x.id===clientId); if (s) return s.name; }
    }
    return '—';
  }

  function startNew(type) {
    setReportType(type);
    setForm({ clientId: type==='performance'?(eligiblePerf[0]?.id||''):(eligibleSocial[0]?.id||''), monthYear:nowMonthYear() });
    setPerfData(emptyPerfData());
    setSocialData(emptySocialData());
    setView('new');
  }

  function saveReport() {
    if (!form.clientId || !form.monthYear) return showToast('Client and month required.', 'error');
    const existing = reports.find(r=>r.clientId===form.clientId&&r.monthYear===form.monthYear&&r.type===reportType);
    if (existing) return showToast('A report already exists for this client and month. Edit the existing report instead.', 'error');
    const entry = {
      id: Date.now().toString(), type: reportType,
      clientId: form.clientId, monthYear: form.monthYear,
      data: reportType==='performance' ? { ...perfData } : { ...socialData },
      createdAt: new Date().toISOString(),
    };
    persist({ ...store, reports: [entry, ...reports] });
    showToast('Report saved.');
    setView('list');
  }

  function updateReport(id, newData) {
    persist({ ...store, reports: reports.map(r=>r.id===id?{...r,data:newData,updatedAt:new Date().toISOString()}:r) });
    showToast('Report updated.');
  }

  function deleteReport(id) {
    if (!window.confirm('Delete this report?')) return;
    persist({ ...store, reports: reports.filter(r=>r.id!==id) }, { deletedIds: { reports: [id] } });
    showToast('Report deleted.');
    if (selectedId===id) setView('list');
  }

  function purgeOldReports(months) {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    const cutoffYM = cutoff.toISOString().slice(0,7); // 'YYYY-MM'
    const toKeep  = reports.filter(r => (r.monthYear || '') >= cutoffYM);
    const removedIds = reports.filter(r => (r.monthYear || '') < cutoffYM).map(r => r.id);
    const removed = reports.length - toKeep.length;
    if (removed === 0) return showToast('No reports older than ' + months + ' months found.');
    if (!window.confirm('Delete ' + removed + ' report' + (removed!==1?'s':'') + ' older than ' + months + ' months? This cannot be undone.')) return;
    persist({ ...store, reports: toKeep }, { deletedIds: { reports: removedIds } });
    showToast(removed + ' report' + (removed!==1?'s':'') + ' deleted.');
  }


  // ── Detail / Edit view — rendered as sub-component to respect Rules of Hooks ──
  if (view === 'detail' && selectedId) {
    const rep = reports.find(r=>r.id===selectedId);
    if (!rep) { setView('list'); return null; }
    const clientReports = reports.filter(r=>r.clientId===rep.clientId&&r.type===rep.type&&r.id!==rep.id);
    return (
      <ReportDetail
        rep={rep}
        clientReports={clientReports}
        clients={clients}
        onBack={()=>setView('list')}
        onDelete={()=>deleteReport(rep.id)}
        onSave={(newData)=>updateReport(rep.id,newData)}
        getClientName={getClientName}
        getClientAdType={getClientAdType}
      />
    );
  }

  // ── New report form ─────────────────────────────────────────────
  if (view === 'new') {
    const eligible = reportType==='performance' ? eligiblePerf : eligibleSocial;
    const adType   = getClientAdType(clients, form.clientId);
    return (
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <Btn size="sm" variant="ghost" onClick={()=>setView('list')}><ChevronLeft size={14}/>Back</Btn>
          <span style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:15,color:'var(--text)'}}>
            {reportType==='social'?'New Social Media Report':'New Performance Marketing Report'}
          </span>
        </div>
        <Card>
          <div style={{padding:'1rem 1.25rem',borderBottom:'1px solid var(--border)'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <FieldWrap label="Client">
                <select style={sel} value={form.clientId} onChange={e=>setForm(p=>({...p,clientId:e.target.value}))}>
                  <option value="">Select client…</option>
                  {eligible.map(c=><option key={c.id} value={c.id}>{c.name}{c.parentName?` (${c.parentName})`:''}</option>)}
                </select>
              </FieldWrap>
              <FieldWrap label="Month">
                <input style={inp} type="month" value={form.monthYear} onChange={e=>setForm(p=>({...p,monthYear:e.target.value}))}/>
              </FieldWrap>
            </div>
          </div>
          <div style={{padding:'1.25rem'}}>
            {reportType==='performance' && (() => {
              const mc = metaClientForId(form.clientId);
              return (
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:16,padding:'12px 14px',background:'rgba(169,137,83,0.12)',border:'1px solid rgba(169,137,83,0.25)',borderRadius:'var(--radius)'}}>
                  <TrendingUp size={16} color="var(--accent2)"/>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--text)',flex:1,minWidth:160}}>
                    {mc ? `Auto-fill the numbers from Meta for ${monthLabel(form.monthYear)}` : 'No Meta ad account mapped for this client'}
                  </span>
                  <Btn size="sm" variant="secondary" disabled={!mc||metaBusy} onClick={async()=>{
                    if(!mc) return;
                    setMetaBusy(true);
                    try {
                      const { since, until } = monthRange(form.monthYear);
                      const res = await fetchMetaInsights({ adAccountId: mc.adAccountId, since, until });
                      setPerfData(prev => ({ ...prev, ...metaToPerfData(res, adType) }));
                      showToast('Filled from Meta — review before saving.');
                    } catch(e){ showToast('Meta fetch failed: '+(e.message||e),'error'); }
                    setMetaBusy(false);
                  }}>{metaBusy?'Fetching…':'Fetch from Meta'}</Btn>
                </div>
              );
            })()}
            {reportType==='performance'
              ? <PerfReportForm data={perfData} setData={setPerfData} adType={adType}/>
              : <SocialReportForm data={socialData} setData={setSocialData}/>
            }
          </div>
          <div style={{padding:'1rem 1.25rem',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:8}}>
            <Btn onClick={()=>setView('list')}>Cancel</Btn>
            <Btn variant="primary" onClick={saveReport}>Save report</Btn>
          </div>
        </Card>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────
  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {/* New report buttons + storage info */}
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        {canSeeSocial && (
          <Btn onClick={()=>startNew('social')}>
            <BarChart2 size={14}/> New Social Media Report
          </Btn>
        )}
        {canSeePerfMarketing && (
          <Btn variant="primary" onClick={()=>startNew('performance')}>
            <TrendingUp size={14}/> New Performance Marketing Report
          </Btn>
        )}
        <div style={{flex:1}}/>
        {/* Purge buttons — manage report storage */}
        <Btn size="sm" variant="ghost" onClick={()=>purgeOldReports(3)}>
          Purge &gt; 3 months
        </Btn>
        <Btn size="sm" variant="ghost" onClick={()=>purgeOldReports(6)}>
          Purge &gt; 6 months
        </Btn>
      </div>

      <Card>
        <SectionHeader title="Reports" count={filtered.length} action={
          <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
            <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{...sel,width:'auto',padding:'4px 10px',fontSize:12}}>
              <option value="all">All types</option>
              {canSeeSocial && <option value="social">Social Media</option>}
              {canSeePerfMarketing && <option value="performance">Performance</option>}
            </select>
            <select value={filterClient} onChange={e=>setFilterClient(e.target.value)} style={{...sel,width:'auto',padding:'4px 10px',fontSize:12}}>
              <option value="">All clients</option>
              {[...eligiblePerf,...eligibleSocial].filter((c,i,a)=>a.findIndex(x=>x.id===c.id)===i).map(c=>(
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        }/>

        {filtered.length === 0
          ? <EmptyState message="No reports yet. Create one above."/>
          : filtered.map(r => (
            <div key={r.id} onClick={()=>{setSelectedId(r.id);setView('detail');}} style={{padding:'12px 18px',borderBottom:'1px solid var(--border)',cursor:'pointer',display:'flex',alignItems:'center',gap:12,transition:'background 0.1s'}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--bg3)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:13,color:'var(--text)',marginBottom:2}}>{getClientName(r.clientId)}</div>
                <div style={{fontSize:12,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',color:'var(--text3)'}}>{monthLabel(r.monthYear)}</div>
              </div>
              <Badge label={r.type==='social'?'Social Media':'Performance'} color={r.type==='social'?'blue':'purple'}/>
              <div style={{fontSize:11,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',color:'var(--text3)',whiteSpace:'nowrap'}}>{new Date(r.createdAt).toLocaleDateString()}</div>
              <ArrowRight size={14} color="var(--text3)"/>
            </div>
          ))
        }
      </Card>
    </div>
  );
}





// ─── Report generation ─────────────────────────────────────────────
function fmtN(v) {
  var n = parseFloat(v);
  return isNaN(n) ? '' : n.toLocaleString();
}

function buildBarSVG(vals, labels, color) {
  color = color || '#C9A227';
  var nums = vals.map(function(v){ return parseFloat(v)||0; });
  var mx   = Math.max.apply(null, nums.concat([1]));
  var W=520, H=158, pl=14, pr=14, pt=30, pb=32;
  var cw   = W - pl - pr;
  var sw   = cw / nums.length;
  var bw   = Math.min(60, sw * 0.5);
  var gid  = 'bar' + Math.round(mx % 99999) + '_' + nums.length + '_' + color.replace('#','');
  // faint horizontal gridlines
  var grid = '';
  for (var g = 1; g <= 3; g++) {
    var gy = pt + (H - pt - pb) * (1 - g/3);
    grid += '<line x1="'+pl+'" y1="'+gy+'" x2="'+(W-pr)+'" y2="'+gy+'" stroke="rgba(239,235,228,0.06)" stroke-width="1"/>';
  }
  var rects = nums.map(function(v,i){
    var bh  = v > 0 ? Math.max(5, Math.round((v/mx)*(H-pt-pb))) : 0;
    var x   = pl + i*sw + (sw-bw)/2;
    var y   = H - pb - bh;
    var lbl = (labels[i]||'').length > 11 ? (labels[i]||'').slice(0,11)+'\u2026' : (labels[i]||'');
    var val = v>=1000000?(v/1000000).toFixed(1)+'M':v>=1000?(v/1000).toFixed(1)+'K':String(v||'');
    return '<rect x="'+x+'" y="'+y+'" width="'+bw+'" height="'+bh+'" rx="2" fill="url(#'+gid+')" data-tip="'+(labels[i]||'')+': '+val+'"/>'
      +'<text x="'+(x+bw/2)+'" y="'+(y-9)+'" text-anchor="middle" font-size="11" font-weight="600" fill="#EFEBE4" font-family="JetBrains Mono,sans-serif">'+val+'</text>'
      +'<text x="'+(x+bw/2)+'" y="'+(H-11)+'" text-anchor="middle" font-size="10" fill="#8A8578" font-family="JetBrains Mono,sans-serif">'+lbl+'</text>';
  }).join('');
  return '<svg viewBox="0 0 '+W+' '+H+'" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:'+H+'px;display:block;">'
    +'<defs><linearGradient id="'+gid+'" x1="0" y1="0" x2="0" y2="1">'
    +'<stop offset="0" stop-color="'+color+'"/><stop offset="1" stop-color="'+color+'" stop-opacity="0.4"/>'
    +'</linearGradient></defs>'
    +grid
    +'<line x1="'+pl+'" y1="'+(H-pb)+'" x2="'+(W-pr)+'" y2="'+(H-pb)+'" stroke="rgba(239,235,228,0.14)" stroke-width="1"/>'
    +rects+'</svg>';
}

// ── Donut chart with legend — segs:[{label,value,color}] ──────────
// footer (optional) {label,value} renders as a total row below the legend.
function donutSVG(segs, centerTop, centerBot, footer) {
  var clean = segs.filter(function(s){ return (parseFloat(s.value)||0) > 0; });
  if (!clean.length) return '';
  var total = clean.reduce(function(s,x){ return s+(parseFloat(x.value)||0); }, 0) || 1;
  var r=52, cx=68, cy=68, sw=18, c=2*Math.PI*r, off=0;
  var rings = clean.map(function(s){
    var len=((parseFloat(s.value)||0)/total)*c;
    var pct=Math.round((parseFloat(s.value)||0)/total*100);
    var el='<circle cx="'+cx+'" cy="'+cy+'" r="'+r+'" fill="none" stroke="'+s.color+'" stroke-width="'+sw+'" stroke-dasharray="'+len.toFixed(2)+' '+(c-len).toFixed(2)+'" stroke-dashoffset="'+(-off).toFixed(2)+'" transform="rotate(-90 '+cx+' '+cy+')" data-tip="'+s.label+': '+fmtN(s.value)+' ('+pct+'%)"/>';
    off+=len; return el;
  }).join('');
  var svg='<svg viewBox="0 0 136 136" xmlns="http://www.w3.org/2000/svg" style="width:136px;height:136px;flex-shrink:0;">'
    +'<circle cx="'+cx+'" cy="'+cy+'" r="'+r+'" fill="none" stroke="rgba(239,235,228,0.06)" stroke-width="'+sw+'"/>'
    +rings
    +(centerTop?'<text x="'+cx+'" y="'+(cy-1)+'" text-anchor="middle" font-size="19" font-weight="700" fill="#EFEBE4" font-family="JetBrains Mono,sans-serif">'+centerTop+'</text>':'')
    +(centerBot?'<text x="'+cx+'" y="'+(cy+15)+'" text-anchor="middle" font-size="8.5" fill="#8A8578" font-family="JetBrains Mono,sans-serif" letter-spacing="1.2">'+centerBot+'</text>':'')
    +'</svg>';
  var legend = clean.map(function(s){
    var pct=Math.round((parseFloat(s.value)||0)/total*100);
    return '<div style="display:flex;align-items:center;gap:9px;margin-bottom:12px;">'
      +'<span style="width:10px;height:10px;border-radius:2px;background:'+s.color+';flex-shrink:0;"></span>'
      +'<span style="flex:1;font-size:12.5px;color:#9A968D;">'+s.label+'</span>'
      +'<span style="font-size:12.5px;font-weight:700;color:#EFEBE4;font-variant-numeric:lining-nums tabular-nums;">'+fmtN(s.value)+'</span>'
      +'<span style="font-size:11px;color:#8A8578;width:36px;text-align:right;">'+pct+'%</span>'
      +'</div>';
  }).join('');
  if (footer) legend += '<div style="display:flex;align-items:center;justify-content:space-between;gap:9px;margin-top:7px;padding-top:12px;border-top:1px solid rgba(239,235,228,0.12);">'
    +'<span style="font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#6E6A61;">'+footer.label+'</span>'
    +'<span style="font-size:15px;font-weight:700;color:#A98953;font-variant-numeric:lining-nums tabular-nums;">'+footer.value+'</span>'
    +'</div>';
  return '<div style="display:flex;align-items:center;gap:28px;flex-wrap:wrap;">'+svg+'<div style="flex:1;min-width:170px;">'+legend+'</div></div>';
}

// ── Trend line chart — vals + month labels ────────────────────────
function lineSVG(vals, labels, color) {
  color = color || '#A98953';
  var nums = vals.map(function(v){ return parseFloat(v)||0; });
  var n=nums.length; if(!n) return '';
  var W=520,H=176,pl=18,pr=16,pt=26,pb=30;
  var mx=Math.max.apply(null,nums.concat([1])), mn=Math.min.apply(null,nums.concat([0]));
  if(mx===mn) mx=mn+1;
  var X=function(i){ return pl + (n<=1 ? (W-pl-pr)/2 : i*(W-pl-pr)/(n-1)); };
  var Y=function(v){ return pt + (1-(v-mn)/(mx-mn))*(H-pt-pb); };
  var grid=''; for(var g=0;g<=3;g++){ var gy=pt+(H-pt-pb)*g/3; grid+='<line x1="'+pl+'" y1="'+gy+'" x2="'+(W-pr)+'" y2="'+gy+'" stroke="rgba(239,235,228,0.06)"/>'; }
  var line=nums.map(function(v,i){ return X(i).toFixed(1)+','+Y(v).toFixed(1); }).join(' ');
  var area='M '+X(0).toFixed(1)+','+(H-pb)+' L '+nums.map(function(v,i){return X(i).toFixed(1)+','+Y(v).toFixed(1);}).join(' L ')+' L '+X(n-1).toFixed(1)+','+(H-pb)+' Z';
  var dots=nums.map(function(v,i){
    var lbl=v>=1000000?(v/1000000).toFixed(1)+'M':v>=1000?(v/1000).toFixed(1)+'K':String(Math.round(v));
    return '<circle cx="'+X(i).toFixed(1)+'" cy="'+Y(v).toFixed(1)+'" r="6" fill="transparent" data-tip="'+(labels[i]||'')+': '+fmtN(v)+'"/>'
      +'<circle cx="'+X(i).toFixed(1)+'" cy="'+Y(v).toFixed(1)+'" r="3.5" fill="'+color+'" stroke="#0A0A0C" stroke-width="1.5" style="pointer-events:none;"/>'
      +'<text x="'+X(i).toFixed(1)+'" y="'+(Y(v)-10).toFixed(1)+'" text-anchor="middle" font-size="10" font-weight="600" fill="#EFEBE4" font-family="JetBrains Mono,sans-serif">'+lbl+'</text>'
      +'<text x="'+X(i).toFixed(1)+'" y="'+(H-10)+'" text-anchor="middle" font-size="9" fill="#8A8578" font-family="JetBrains Mono,sans-serif">'+(labels[i]||'')+'</text>';
  }).join('');
  var gid='ln'+Math.round(mx%99999)+n;
  return '<svg viewBox="0 0 '+W+' '+H+'" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:'+H+'px;display:block;">'
    +'<defs><linearGradient id="'+gid+'" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="'+color+'" stop-opacity="0.3"/><stop offset="1" stop-color="'+color+'" stop-opacity="0"/></linearGradient></defs>'
    +grid+'<path d="'+area+'" fill="url(#'+gid+')"/>'
    +'<polyline points="'+line+'" fill="none" stroke="'+color+'" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>'
    +dots+'</svg>';
}

// ── Section header for a chart card ───────────────────────────────
function chartCard(label, inner) {
  return '<div style="background:#141417;border:1px solid rgba(239,235,228,0.12);border-radius:2px;padding:20px 22px;page-break-inside:avoid;margin-bottom:14px;">'
    +'<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#6E6A61;margin-bottom:14px;">'+label+'</div>'
    +inner+'</div>';
}

function generateReportHTML(rep, clientName, history, opts) {
  var interactive = !!(opts && opts.interactive);
  var d       = rep.data;
  var isPM    = rep.type === 'performance';
  var title   = isPM ? 'Performance Marketing Report' : 'Social Media Report';
  var mon     = monthLabel(rep.monthYear);
  var yr      = new Date().getFullYear();
  var genDate = new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'});
  var GOLD    = '#A98953';   // warm gold
  var INK     = '#0A0A0C';   // warm near-black
  var SURF    = '#141417';   // warm charcoal card
  var SURF2   = '#17171A';   // header strips inside cards
  var LINE    = 'rgba(239,235,228,0.12)';
  var TXT     = '#EFEBE4';   // warm ivory
  var MUT     = '#9A968D';   // warm grey
  var FAINT   = '#6E6A61';
  var DARK    = INK;         // legacy alias used below
  var COLORS  = [GOLD,'#B58060','#8FB08A','#B89A6A','#A87E8E','#6E8894']; // earthy editorial palette
  // Fine film-grain — warm-tinted noise for a printed, crafted feel.
  // url() uses single quotes + %22-encoded SVG attrs so it stays valid inside style="...".
  var NOISE   = "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22180%22 height=%22180%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix type=%22matrix%22 values=%220 0 0 0 0.85 0 0 0 0 0.78 0 0 0 0 0.6 0 0 0 0.05 0%22/></filter><rect width=%22180%22 height=%22180%22 filter=%22url(%23n)%22/></svg>')";

  // ── Month-over-month trend data (from the client's report history) ─
  var hist = (history && history.length) ? history.slice() : [rep];
  hist.sort(function(a,b){ return (a.monthYear||'').localeCompare(b.monthYear||''); });
  if (hist.length > 6) hist = hist.slice(hist.length - 6);
  var trendLabels = hist.map(function(h){ return monthLabel(h.monthYear).split(' ')[0]; });
  function trendSeries(fn){ return hist.map(function(h){ return fn(h.data||{}); }); }
  var hasTrend = hist.length >= 2;

  // ── Inline CSS — full-bleed, dark editorial ─────────────────────
  var css = [
    '@import url("https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,500;1,6..96,400&family=Inter+Tight:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap");',
    '@page { margin: 0; size: A4; }',
    '*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }',
    'html, body { background: '+INK+'; }',
    'body { color: '+TXT+'; font-family: "Inter Tight", "Helvetica Neue", Arial, sans-serif; font-weight: 300; -webkit-font-smoothing: antialiased; line-height: 1.55; font-size: 13px; letter-spacing: 0.01em; }',
    'a { color: #A98953; text-decoration: none; word-break: break-all; }',
    'table { border-collapse: collapse; width: 100%; }',
    '.serif { font-family: "Bodoni Moda", "Didot", Georgia, serif; font-weight: 400; }',
    '@media print {',
    '  @page { margin: 0; size: A4; }',
    '  html, body { background: '+INK+' !important; }',
    '  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }',
    '}',
  ].join('\n');

  // ── Interactive layer (shared web link) — animations on screen only,
  //    plus a floating Download-PDF button. None of it affects the print/PDF.
  if (interactive) {
    css += '\n@media screen {'
      + ' body{animation:rUp .55s ease both;}'
      + ' @keyframes rUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:none;}}'
      + ' svg rect{transform-box:fill-box;transform-origin:50% 100%;animation:rGrow .9s cubic-bezier(.2,.8,.2,1) both;}'
      + ' @keyframes rGrow{from{transform:scaleY(0.001);}to{transform:scaleY(1);}}'
      + ' svg polyline{stroke-dasharray:1700;stroke-dashoffset:1700;animation:rDraw 1.9s ease .25s forwards;}'
      + ' @keyframes rDraw{to{stroke-dashoffset:0;}}'
      + ' svg circle{animation:rFade 1s ease both;} @keyframes rFade{from{opacity:0;}to{opacity:1;}}'
      + ' [data-tip]{cursor:pointer;transition:filter .12s,opacity .12s;}'
      + ' [data-tip]:hover{filter:brightness(1.35);}'
      + ' #pdfbtn{position:fixed;top:20px;right:20px;z-index:99;display:inline-flex;align-items:center;gap:8px;background:'+GOLD+';color:#0A0A0C;border:1px solid '+GOLD+';border-radius:2px;padding:12px 18px;font-family:"JetBrains Mono",monospace;font-size:10px;font-weight:400;letter-spacing:0.14em;text-transform:uppercase;cursor:pointer;box-shadow:0 10px 28px rgba(0,0,0,0.45);transition:transform .15s,filter .15s;}'
      + ' #pdfbtn:hover{transform:translateY(-1px);filter:brightness(1.07);}'
      + '}'
      + '\n@media print { #pdfbtn{display:none !important;} }';
  }

  // ── Helper: section header ──────────────────────────────────────
  // No emojis — uses a small square number badge instead
  var sectionCount = 0;
  function sec(title2, content) {
    sectionCount++;
    var n = sectionCount < 10 ? '0'+sectionCount : String(sectionCount);
    return '<div style="page-break-inside:avoid;padding-top:44px;">'
      +'<div style="display:flex;align-items:baseline;gap:16px;margin-bottom:20px;">'
      +'<div class="serif" style="font-size:34px;color:'+GOLD+';line-height:0.9;flex-shrink:0;">'+n+'</div>'
      +'<div style="font-size:11px;font-weight:700;letter-spacing:4px;text-transform:uppercase;color:'+TXT+';">'+title2+'</div>'
      +'<div style="flex:1;height:1px;background:'+LINE+';"></div>'
      +'</div>'
      +content
      +'</div>';
  }

  // ── Helper: KPI card ────────────────────────────────────────────
  function kpi(label, val, sub, color) {
    color = color || GOLD;
    return '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;padding:20px 22px;flex:1;min-width:120px;">'
      +'<div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:'+FAINT+';margin-bottom:10px;">'+label+'</div>'
      +'<div class="serif" style="font-size:28px;color:'+color+';line-height:1;margin-bottom:5px;">'+val+'</div>'
      +(sub?'<div style="font-size:11px;color:'+MUT+';">'+sub+'</div>':'')
      +'</div>';
  }

  // ── Helper: card wrapper ────────────────────────────────────────
  function card(content, pad) {
    return '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;overflow:hidden;'
      +(pad?'padding:'+pad+';':'')+'page-break-inside:avoid;">'+content+'</div>';
  }

  // ── Helper: metric row ──────────────────────────────────────────
  function row(label, val, bold) {
    if (!val) return '';
    return '<tr>'
      +'<td style="padding:11px 20px;color:'+MUT+';border-bottom:1px solid '+LINE+';width:54%;">'+label+'</td>'
      +'<td style="padding:11px 20px;font-weight:'+(bold?'700':'500')+';color:'+(bold?GOLD:TXT)+';text-align:right;border-bottom:1px solid '+LINE+';font-variant-numeric:lining-nums tabular-nums;">'+val+'</td>'
      +'</tr>';
  }

  // ── Helper: campaign card ───────────────────────────────────────
  function campCard(camp, idx, totalSpent) {
    var pct = totalSpent && camp.spent
      ? Math.round((parseFloat(camp.spent)/parseFloat(totalSpent))*100) : null;
    var rows = [
      row('Objective', camp.objective),
      row('Amount Spent', camp.spent?'PKR '+fmtN(camp.spent):'', true),
      row('Cost Per Goal', camp.cpg?'PKR '+fmtN(camp.cpg):''),
      row('Impressions', camp.impressions?fmtN(camp.impressions):''),
      row('Clicks', camp.clicks?fmtN(camp.clicks):''),
      row('CTR', camp.ctr?camp.ctr+'%':''),
      row('CPM', camp.cpm?'PKR '+fmtN(camp.cpm):''),
      camp.roas     ? row('ROAS', camp.roas+'x', true) : '',
      camp.cpv      ? row('Cost Per Visit', 'PKR '+fmtN(camp.cpv)) : '',
      camp.cpl      ? row('Cost Per Lead',  'PKR '+fmtN(camp.cpl), true) : '',
      camp.convRate ? row('Conversion Rate', camp.convRate+'%') : '',
    ].filter(Boolean).join('');

    var n = (idx+1) < 10 ? '0'+(idx+1) : String(idx+1);
    return '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;overflow:hidden;margin-bottom:14px;page-break-inside:avoid;">'
      // Header strip
      +'<div style="background:'+SURF2+';padding:16px 20px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid '+LINE+';">'
      +'<div>'
      +'<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:4px;">Campaign '+n+'</div>'
      +'<div class="serif" style="font-size:20px;color:'+TXT+';">'+( camp.name || 'Unnamed' )+'</div>'
      +'</div>'
      +(pct!==null
        ?'<div style="text-align:right;">'
        +'<div style="font-size:9px;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:2px;">Budget share</div>'
        +'<div class="serif" style="font-size:26px;color:'+GOLD+';">'+pct+'%</div>'
        +'</div>' : '')
      +'</div>'
      +'<table>'+rows+'</table>'
      +(camp.notes
        ?'<div style="padding:12px 20px;background:rgba(239,235,228,0.04);font-size:12px;color:'+MUT+';border-top:1px solid '+LINE+';font-style:italic;white-space:pre-wrap;">'+camp.notes+'</div>'
        :'')
      +'</div>';
  }

  // ── Build body ──────────────────────────────────────────────────
  var body = '';

  if (isPM) {

    // KPIs shown on cover page — not repeated in body

    // Campaign section
    if (d.campaigns && d.campaigns.length > 0) {
      var campHTML = '';

      // Horizontal budget bar (clean, no SVG text issues)
      if (d.campaigns.length > 1) {
        var tot = d.campaigns.reduce(function(s,c){ return s+(parseFloat(c.spent)||0); },0);
        var barSegs = d.campaigns.map(function(c,i){
          var pct2 = tot > 0 ? Math.round((parseFloat(c.spent)||0)/tot*100) : 0;
          return '<div style="flex:'+pct2+';min-width:1px;height:36px;background:'+COLORS[i%COLORS.length]+';display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;color:'+(i===0?DARK:'#EFEBE4')+';overflow:hidden;white-space:nowrap;padding:0 8px;">'+pct2+'%</div>';
        }).join('');
        var legend = d.campaigns.map(function(c,i){
          return '<div style="display:flex;align-items:center;gap:6px;margin-right:16px;margin-bottom:6px;">'
            +'<div style="width:10px;height:10px;border-radius:2px;background:'+COLORS[i%COLORS.length]+';flex-shrink:0;"></div>'
            +'<div style="font-size:11px;color:#4C483F;">'+(c.name||'Camp '+(i+1))+' &mdash; PKR '+fmtN(c.spent)+'</div>'
            +'</div>';
        }).join('');
        campHTML += '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;padding:18px 20px;margin-bottom:14px;page-break-inside:avoid;">'
          +'<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:12px;">Budget Distribution</div>'
          +'<div style="display:flex;border-radius:2px;overflow:hidden;margin-bottom:12px;">'+barSegs+'</div>'
          +'<div style="display:flex;flex-wrap:wrap;">'+legend+'</div>'
          +'</div>';
      }

      // Impressions bar chart
      var impCamps = d.campaigns.filter(function(c){ return parseFloat(c.impressions)>0; });
      if (impCamps.length > 0) {
        campHTML += '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;padding:18px 20px;margin-bottom:14px;page-break-inside:avoid;">'
          +'<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:12px;">Impressions by Campaign</div>'
          +buildBarSVG(
              impCamps.map(function(c){return c.impressions;}),
              impCamps.map(function(c){return c.name||'Camp';}),
              '#A98953'
            )
          +'</div>';
      }

      // Campaign cards
      campHTML += d.campaigns.map(function(c,i){ return campCard(c, i, d.totalSpent); }).join('');
      body += sec('Campaign Breakdown', campHTML);
    }

    // Spend distribution donut + ROAS bars + funnel
    if (d.campaigns && d.campaigns.length) {
      var spendSegs = d.campaigns.map(function(c,i){ return {label:c.name||'Campaign '+(i+1), value:parseFloat(c.spent)||0, color:COLORS[i%COLORS.length]}; });
      var spendTot = spendSegs.reduce(function(s,x){ return s+x.value; }, 0);
      if (spendTot>0) body += sec('Spend Distribution', chartCard('Budget by campaign', donutSVG(spendSegs, '', '', {label:'Total Spend', value:'PKR '+fmtN(d.totalSpent||spendTot)})));

      var roasCamps = d.campaigns.filter(function(c){ return parseFloat(c.roas)>0; });
      if (roasCamps.length) body += sec('Return on Ad Spend', chartCard('ROAS by campaign (x)', buildBarSVG(roasCamps.map(function(c){return c.roas;}), roasCamps.map(function(c){return c.name||'Camp';}), '#8FB08A')));

      var totImp = d.campaigns.reduce(function(s,c){ return s+(parseFloat(c.impressions)||0); }, 0);
      var totClk = d.campaigns.reduce(function(s,c){ return s+(parseFloat(c.clicks)||0); }, 0);
      var totLead= d.campaigns.reduce(function(s,c){ var cpl=parseFloat(c.cpl), sp=parseFloat(c.spent); return s + (cpl>0&&sp>0 ? Math.round(sp/cpl) : 0); }, 0);
      if (totImp>0 && totClk>0) {
        var stages = [['Impressions',totImp,'#6E8894'],['Clicks',totClk,GOLD]];
        if (totLead>0) stages.push(['Leads',totLead,'#8FB08A']);
        var top = stages[0][1];
        var funnel = stages.map(function(s,i){
          var pct = Math.round(Math.sqrt(s[1]/top)*100);   // sqrt scale so big drop-offs stay legible
          var conv = i>0 ? Math.round(s[1]/stages[i-1][1]*100) : null;
          return '<div style="margin-bottom:14px;">'
            +'<div style="display:flex;justify-content:space-between;align-items:baseline;font-size:11px;color:'+MUT+';margin-bottom:6px;"><span style="text-transform:uppercase;letter-spacing:1.5px;font-weight:600;">'+s[0]+'</span><span style="color:'+TXT+';font-weight:700;font-size:13px;">'+fmtN(s[1])+(conv!=null?' <span style="color:'+FAINT+';font-weight:500;">('+conv+'% of prev)</span>':'')+'</span></div>'
            +'<div style="height:30px;border-radius:2px;background:'+s[2]+';width:'+Math.max(6,pct)+'%;min-width:40px;"></div>'
            +'</div>';
        }).join('');
        body += sec('Conversion Funnel', chartCard('Impressions to outcomes', funnel));
      }
    }

    // Month-over-month spend trend
    if (hasTrend) {
      var spendT = trendSeries(function(x){ return parseFloat(x.totalSpent)||0; });
      var salesT = trendSeries(function(x){ return parseFloat(x.netSales)||0; });
      var pT = '';
      if (spendT.some(function(v){return v>0;})) pT += chartCard('Total Spend · last '+hist.length+' months', lineSVG(spendT, trendLabels, GOLD));
      if (salesT.some(function(v){return v>0;})) pT += chartCard('Net Sales · last '+hist.length+' months', lineSVG(salesT, trendLabels, '#8FB08A'));
      if (pT) body += sec('Trends', pT);
    }

    // Top Ad
    if (d.topAd && (d.topAd.description || d.topAd.links || d.topAd.comments)) {
      var ta = '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;padding:22px;page-break-inside:avoid;">';
      if (d.topAd.description) {
        ta += '<p style="font-size:14px;color:'+TXT+';line-height:1.75;margin-bottom:16px;white-space:pre-wrap;">'+d.topAd.description+'</p>';
      }
      if (d.topAd.links) {
        ta += '<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:8px;">Content Links</div>';
        d.topAd.links.split('\n').filter(function(l){return l.trim();}).forEach(function(l){
          ta += '<a href="'+l.trim()+'" style="display:block;font-size:13px;color:#A98953;margin-bottom:4px;word-break:break-all;">'+l.trim()+'</a>';
        });
      }
      if (d.topAd.comments) {
        ta += '<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin:16px 0 8px;">Comments &amp; Next Steps</div>'
          +'<div style="border-left:3px solid '+GOLD+';padding:10px 14px;background:rgba(239,235,228,0.05);">'
          +'<p style="font-size:13px;color:'+TXT+';line-height:1.7;white-space:pre-wrap;">'+d.topAd.comments+'</p>'
          +'</div>';
      }
      ta += '</div>';
      body += sec('Top Performing Ad', ta);
    }

    // Suggestions
    if (d.suggestions) {
      body += sec('Suggestions for Next Month',
        '<div style="position:relative;overflow:hidden;background:'+SURF+' '+NOISE+';background-size:170px;border:1px solid '+LINE+';border-radius:2px;padding:38px 42px 34px;page-break-inside:avoid;">'
        +'<div class="serif" style="font-size:40px;line-height:0.4;color:'+GOLD+';opacity:0.55;margin-bottom:16px;">“</div>'
        +'<p class="serif" style="font-size:19px;font-style:italic;color:'+TXT+';line-height:1.7;white-space:pre-wrap;">'+(d.suggestions||'').replace(/\s+(\d{1,2}[.)]\s)/g,'\n$1').trim()+'</p>'
        +'</div>'
      );
    }

  } else {

    // ── SOCIAL ───────────────────────────────────────────────────
    var fb  = parseFloat(d.viewsFB)||0,  ig  = parseFloat(d.viewsIG)||0;
    var rfb = parseFloat(d.reachFB)||0,  rig = parseFloat(d.reachIG)||0;
    var ifb = parseFloat(d.interactionsFB)||0, iig = parseFloat(d.interactionsIG)||0;
    var ffb = parseFloat(d.followsFB)||0, fig = parseFloat(d.followsIG)||0;

    // KPIs shown on cover page — not repeated in body

    // Charts
    var charts = '';
    if (fb||ig)   charts += '<div style="flex:1;min-width:200px;"><div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:10px;">Views</div>'+buildBarSVG([fb,ig],['Facebook','Instagram'],'#A98953')+'</div>';
    if (rfb||rig) charts += '<div style="flex:1;min-width:200px;"><div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:10px;">Reach</div>'+buildBarSVG([rfb,rig],['Facebook','Instagram'],'#8FB08A')+'</div>';
    if (charts) {
      body += sec('Platform Performance',
        '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;padding:20px;page-break-inside:avoid;">'
        +'<div style="display:flex;gap:20px;flex-wrap:wrap;">'+charts+'</div>'
        +'</div>'
      );
    }

    // Donut breakdowns — reach split + engagement mix
    var sDon = '';
    if (rfb||rig) sDon += chartCard('Reach by Platform', donutSVG([{label:'Facebook',value:rfb,color:'#6E8894'},{label:'Instagram',value:rig,color:'#A87E8E'}], '', '', {label:'Total Reach', value:fmtN(rfb+rig)}));
    var engSegs = [{label:'Interactions',value:ifb+iig,color:GOLD},{label:'New Follows',value:ffb+fig,color:'#8FB08A'},{label:'Messages',value:(parseFloat(d.msgConvosFB)||0)+(parseFloat(d.msgConvosIG)||0),color:'#B58060'}];
    if (engSegs.some(function(s){return s.value>0;})) sDon += chartCard('Engagement Mix', donutSVG(engSegs));
    if (sDon) body += sec('Audience Breakdown', sDon);

    // Month-over-month growth trend
    if (hasTrend) {
      var reachT  = trendSeries(function(x){ return (parseFloat(x.reachFB)||0)+(parseFloat(x.reachIG)||0); });
      var viewsT  = trendSeries(function(x){ return (parseFloat(x.viewsFB)||0)+(parseFloat(x.viewsIG)||0); });
      var followT = trendSeries(function(x){ return (parseFloat(x.followsFB)||0)+(parseFloat(x.followsIG)||0); });
      var sT = '';
      if (viewsT.some(function(v){return v>0;}))  sT += chartCard('Total Views · last '+hist.length+' months', lineSVG(viewsT, trendLabels, '#6E8894'));
      if (reachT.some(function(v){return v>0;}))  sT += chartCard('Total Reach · last '+hist.length+' months', lineSVG(reachT, trendLabels, GOLD));
      if (followT.some(function(v){return v>0;})) sT += chartCard('New Follows · last '+hist.length+' months', lineSVG(followT, trendLabels, '#8FB08A'));
      if (sT) body += sec('Growth Trend', sT);
    }

    // Metrics table
    var metRows = [
      ['Views',               d.viewsFB,        d.viewsIG,       fb+ig],
      ['Reach',               d.reachFB,        d.reachIG,       rfb+rig],
      ['Content Interactions',d.interactionsFB, d.interactionsIG,ifb+iig],
      ['Follows',             d.followsFB,      d.followsIG,     ffb+fig],
      ['Messaging Convos',    d.msgConvosFB,    d.msgConvosIG,   (parseFloat(d.msgConvosFB)||0)+(parseFloat(d.msgConvosIG)||0)],
    ].filter(function(r){ return parseFloat(r[1])||parseFloat(r[2]); });

    if (metRows.length) {
      var thead = '<tr style="background:'+SURF2+';">'
        +'<th style="padding:10px 18px;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';text-align:left;">Metric</th>'
        +'<th style="padding:10px 18px;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#C6A876;text-align:right;">Facebook</th>'
        +'<th style="padding:10px 18px;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#A9C0A4;text-align:right;">Instagram</th>'
        +'<th style="padding:10px 18px;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+GOLD+';text-align:right;">Total</th>'
        +'</tr>';
      var tbody = metRows.map(function(r){
        return '<tr>'
          +'<td style="padding:10px 18px;color:'+MUT+';border-bottom:1px solid '+LINE+';">'+r[0]+'</td>'
          +'<td style="padding:10px 18px;color:'+TXT+';text-align:right;border-bottom:1px solid '+LINE+';">'+fmtN(r[1])+'</td>'
          +'<td style="padding:10px 18px;color:'+TXT+';text-align:right;border-bottom:1px solid '+LINE+';">'+fmtN(r[2])+'</td>'
          +'<td style="padding:10px 18px;font-weight:700;color:'+GOLD+';text-align:right;border-bottom:1px solid '+LINE+';font-variant-numeric:lining-nums tabular-nums;">'+fmtN(r[3])+'</td>'
          +'</tr>';
      }).join('');
      body += sec('Detailed Metrics',
        '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;overflow:hidden;page-break-inside:avoid;">'
        +'<table>'+thead+tbody+'</table>'
        +'</div>'
      );
    }

    // Published content
    var pub = [['FB Posts',d.publishedFBPosts],['FB Stories',d.publishedFBStories],['IG Posts',d.publishedIGPosts],['IG Stories',d.publishedIGStories]].filter(function(x){return x[1];});
    if (pub.length) {
      body += sec('Published Content',
        '<div style="background:'+SURF+';border:1px solid '+LINE+';border-radius:2px;overflow:hidden;page-break-inside:avoid;">'
        +'<table>'+pub.map(function(x){ return row(x[0],x[1]); }).join('')+'</table>'
        +'</div>'
      );
    }

    // Top posts & stories
    var posts   = (d.topPostsFB||[]).filter(Boolean).map(function(p){return{p:p,pl:'Facebook'};}).concat((d.topPostsIG||[]).filter(Boolean).map(function(p){return{p:p,pl:'Instagram'};}));
    var stories = (d.topStoriesFB||[]).filter(Boolean).map(function(p){return{p:p,pl:'Facebook'};}).concat((d.topStoriesIG||[]).filter(Boolean).map(function(p){return{p:p,pl:'Instagram'};}));

    function topList(items, headLabel) {
      if (!items.length) return '';
      var rows2 = items.map(function(item, i){
        return '<div style="display:flex;align-items:flex-start;gap:12px;padding:12px 18px;border-bottom:1px solid '+LINE+';background:'+SURF+';">'
          +'<div style="width:22px;height:22px;background:'+GOLD+';border-radius:50%;color:'+DARK+';font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;">'+(i+1)+'</div>'
          +'<div>'
          +'<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:'+FAINT+';margin-bottom:3px;">'+item.pl+'</div>'
          +(item.p.startsWith('http')
            ?'<a href="'+item.p+'" style="font-size:12px;color:#A98953;word-break:break-all;">'+item.p+'</a>'
            :'<div style="font-size:13px;color:'+TXT+';">'+item.p+'</div>')
          +'</div></div>';
      }).join('');
      return '<div style="margin-bottom:10px;border-radius:2px;overflow:hidden;page-break-inside:avoid;">'
        +'<div style="background:'+SURF2+';padding:11px 20px;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#78746A;">'+headLabel+'</div>'
        +rows2+'</div>';
    }

    var topContent = topList(posts,'Top Posts & Reels') + topList(stories,'Top Stories');
    if (topContent) body += sec('Top Performing Content', topContent);

    // Suggestions
    if (d.suggestions) {
      body += sec('Suggestions for Next Month',
        '<div style="position:relative;overflow:hidden;background:'+SURF+' '+NOISE+';background-size:170px;border:1px solid '+LINE+';border-radius:2px;padding:38px 42px 34px;">'
        +'<div class="serif" style="font-size:40px;line-height:0.4;color:'+GOLD+';opacity:0.55;margin-bottom:16px;">“</div>'
        +'<p class="serif" style="font-size:19px;font-style:italic;color:'+TXT+';line-height:1.7;white-space:pre-wrap;">'+(d.suggestions||'').replace(/\s+(\d{1,2}[.)]\s)/g,'\n$1').trim()+'</p>'
        +'</div>'
      );
    }
  }

  // ── Assemble HTML ───────────────────────────────────────────────
  return '<!DOCTYPE html>\n<html lang="en">\n<head>\n'
    +'<meta charset="UTF-8"/>\n'
    +'<meta name="viewport" content="width=device-width,initial-scale=1"/>\n'
    +'<title>'+clientName+' — '+title+' — '+mon+'</title>\n'
    +'<style>\n'+css+'\n</style>\n'
    +'</head>\n<body>\n'
    +(interactive ? '<button id="pdfbtn" onclick="window.print()">↓ Download PDF</button>\n' : '')

    // Cover — full dark page with summary content
    +(function(){
      // Build cover summary stats based on report type
      var statsHTML = '';
      if (isPM) {
        var statItems = [];
        if (d.dateRange)       statItems.push(['Date Range',   d.dateRange]);
        if (d.totalSpent)      statItems.push(['Total Spend',  'PKR '+fmtN(d.totalSpent)]);
        if (d.overallCPL)      statItems.push(['Overall CPL',  'PKR '+fmtN(d.overallCPL)]);
        if (d.netSales)        statItems.push(['Net Sales',    'PKR '+fmtN(d.netSales)]);
        if (d.totalSalesBefore)statItems.push(['Gross Sales',  'PKR '+fmtN(d.totalSalesBefore)]);
        if (d.campaigns && d.campaigns.length) statItems.push(['Campaigns', String(d.campaigns.length)]);
        if (statItems.length > 0) {
          statsHTML = '<div style="margin-top:52px;">'
            +'<div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#9C978C;margin-bottom:18px;">Report Summary</div>'
            +'<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:#232320;border-radius:2px;overflow:hidden;">'
            +statItems.slice(0,6).map(function(s){
              return '<div style="background:#17171A;padding:18px 20px;">'
                +'<div style="font-size:9px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:#8A857B;margin-bottom:7px;">'+s[0]+'</div>'
                +'<div style="font-family:\'Bodoni Moda\',Georgia,serif;font-size:18px;color:#EFEBE4;line-height:1.2;">'+s[1]+'</div>'
                +'</div>';
            }).join('')
            +'</div></div>';
        }
        // Campaign list on cover
        if (d.campaigns && d.campaigns.length > 0) {
          statsHTML += '<div style="margin-top:28px;">'
            +'<div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#9C978C;margin-bottom:14px;">Campaigns</div>'
            +d.campaigns.map(function(c,i){
              var pct = d.totalSpent && c.spent ? Math.round((parseFloat(c.spent)/parseFloat(d.totalSpent))*100) : null;
              return '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #232320;">'
                +'<div style="display:flex;align-items:center;gap:12px;">'
                +'<div style="width:6px;height:6px;border-radius:50%;background:'+GOLD+';flex-shrink:0;"></div>'
                +'<span style="font-size:13px;color:#CFC9BE;">'+(c.name||'Campaign '+(i+1))+'</span>'
                +'</div>'
                +'<div style="display:flex;align-items:center;gap:16px;">'
                +(c.spent?'<span style="font-size:12px;color:'+FAINT+';">PKR '+fmtN(c.spent)+'</span>':'')
                +(pct!==null?'<span style="font-size:12px;font-weight:600;color:'+GOLD+';">'+pct+'%</span>':'')
                +'</div>'
                +'</div>';
            }).join('')
            +'</div>';
        }
        // KPI cards on cover — dark style versions
        var coverKpiPM = [];
        if (d.totalSpent)       coverKpiPM.push(['Total Spend',  'PKR '+fmtN(d.totalSpent),       d.dateRange||'', GOLD]);
        if (d.totalSalesBefore) coverKpiPM.push(['Gross Sales',  'PKR '+fmtN(d.totalSalesBefore), 'Before returns', '#8FB08A']);
        if (d.netSales)         coverKpiPM.push(['Net Sales',    'PKR '+fmtN(d.netSales),         'After returns',  '#8FB08A']);
        if (d.overallCPL)       coverKpiPM.push(['Overall CPL',  'PKR '+fmtN(d.overallCPL),       'Cost Per Lead',  '#A98953']);
        if (coverKpiPM.length > 0) {
          statsHTML += '<div style="display:grid;grid-template-columns:repeat('+Math.min(coverKpiPM.length,3)+',1fr);gap:10px;margin-top:28px;">'
            +coverKpiPM.map(function(k){
              return '<div style="background:#0F0F12;border:1px solid #232320;border-radius:2px;padding:16px 18px;">'
                +'<div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#78746A;margin-bottom:8px;">'+k[0]+'</div>'
                +'<div style="font-family:Bodoni Moda,Georgia,serif;font-size:22px;color:'+k[3]+';line-height:1;margin-bottom:4px;">'+k[1]+'</div>'
                +(k[2]?'<div style="font-size:10px;color:#6A665D;">'+k[2]+'</div>':'')
                +'</div>';
            }).join('')
            +'</div>';
        }
      } else {
        // Social media cover summary
        var fb=parseFloat(d.viewsFB)||0, ig=parseFloat(d.viewsIG)||0;
        var rfb=parseFloat(d.reachFB)||0, rig=parseFloat(d.reachIG)||0;
        var ifb=parseFloat(d.interactionsFB)||0, iig=parseFloat(d.interactionsIG)||0;
        var ffb=parseFloat(d.followsFB)||0, fig=parseFloat(d.followsIG)||0;
        var socialStats = [];
        if (fb||ig)   socialStats.push(['Total Views',        fmtN(fb+ig)]);
        if (rfb||rig) socialStats.push(['Total Reach',        fmtN(rfb+rig)]);
        if (ifb||iig) socialStats.push(['Interactions',       fmtN(ifb+iig)]);
        if (ffb||fig) socialStats.push(['New Follows',        fmtN(ffb+fig)]);
        var fbPosts = [d.publishedFBPosts, d.publishedIGPosts].filter(Boolean);
        if (fbPosts.length) socialStats.push(['Posts Published', fbPosts.reduce(function(s,v){return s+(parseInt(v)||0);},0).toString()]);
        if (socialStats.length > 0) {
          statsHTML = '<div style="margin-top:52px;">'
            +'<div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#9C978C;margin-bottom:18px;">Report Summary</div>'
            +'<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:#232320;border-radius:2px;overflow:hidden;">'
            +socialStats.slice(0,6).map(function(s){
              return '<div style="background:#17171A;padding:18px 20px;">'
                +'<div style="font-size:9px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:#8A857B;margin-bottom:7px;">'+s[0]+'</div>'
                +'<div style="font-family:\'Bodoni Moda\',Georgia,serif;font-size:18px;color:#EFEBE4;line-height:1.2;">'+s[1]+'</div>'
                +'</div>';
            }).join('')
            +'</div></div>';
        }
      }

      return '<div style="background:'+INK+' '+NOISE+';background-size:170px;height:297mm;padding:54px 58px;display:flex;flex-direction:column;position:relative;overflow:hidden;">'
        // Soft gold glow, top-right
        +'<div style="position:absolute;top:-150px;right:-150px;width:440px;height:440px;border-radius:50%;background:radial-gradient(circle,rgba(169,137,83,0.18),rgba(169,137,83,0) 70%);"></div>'
        // Vertical spine label
        +'<div style="position:absolute;left:20px;top:0;bottom:0;display:flex;align-items:center;"><div style="writing-mode:vertical-rl;transform:rotate(180deg);font-size:8.5px;font-weight:700;letter-spacing:4px;text-transform:uppercase;color:'+FAINT+';">'+title+'</div></div>'
        // Top bar
        +'<div style="display:flex;align-items:flex-start;justify-content:space-between;position:relative;">'
        +'<div style="font-size:9px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:'+MUT+';">'+title+'</div>'
        +'<div style="display:flex;flex-direction:column;align-items:flex-end;">'
        +'<div class="serif" style="font-size:24px;letter-spacing:0.24em;text-indent:0.24em;color:'+TXT+';line-height:1;">STR</div>'
        +'<div style="width:26px;height:1px;background:'+GOLD+';opacity:0.6;margin:9px 0 6px;"></div>'
        +'<div style="font-family:\'JetBrains Mono\',monospace;font-size:8px;letter-spacing:0.22em;text-transform:uppercase;color:'+FAINT+';">Strategists · strategistspk.com</div>'
        +'</div>'
        +'</div>'
        // Gold rule
        +'<div style="height:1px;background:linear-gradient(90deg,'+GOLD+',rgba(169,137,83,0.06));margin-top:22px;"></div>'
        // Headline
        +'<div style="flex:1;display:flex;flex-direction:column;justify-content:center;padding:28px 0;position:relative;">'
        +'<div style="font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:'+FAINT+';margin-bottom:18px;">Monthly Performance Review</div>'
        +'<div class="serif" style="font-size:62px;color:'+TXT+';line-height:0.98;letter-spacing:-1.5px;margin-bottom:18px;">'+clientName+'</div>'
        +'<div class="serif" style="font-size:24px;color:'+GOLD+';font-style:italic;margin-bottom:14px;">'+mon+'</div>'
        +'<div style="font-size:11px;color:'+MUT+';">Prepared by Strategists · '+genDate+'</div>'
        // Summary stats grid + campaign list
        +statsHTML
        +'</div>'
        // Bottom bar
        +'<div style="border-top:1px solid '+LINE+';padding-top:18px;display:flex;align-items:center;justify-content:space-between;position:relative;">'
        +'<div style="font-size:9px;color:'+FAINT+';letter-spacing:1.5px;text-transform:uppercase;">Confidential · Strategists Business Manager</div>'
        +'<div class="serif" style="font-size:14px;color:'+GOLD+';">01</div>'
        +'</div>'
        +'</div>\n'
        // Force page break before body
        +'<div style="page-break-before:always;"></div>\n';
    })()

    // Body — full-bleed ink with grain
    +'<div style="background:'+INK+' '+NOISE+';background-size:170px;padding:0 58px 40px;">'
    +body
    +'</div>\n'

    // Footer
    +'<div style="background:'+INK+';border-top:1px solid '+LINE+';padding:18px 56px;text-align:center;">'
    +'<div style="font-size:10px;color:'+FAINT+';letter-spacing:0.5px;">\u00a9 '+yr+' Strategists \u00b7 strategistspk.com</div>'
    +'</div>\n'

    +(interactive ?
      '<script>(function(){'
      + 'var tip=document.createElement("div");'
      + 'tip.style.cssText="position:fixed;z-index:9999;pointer-events:none;background:#17171A;color:#EFEBE4;border:1px solid rgba(239,235,228,0.20);border-radius:2px;padding:7px 11px;font:600 12px/1.2 \'JetBrains Mono\',sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.55);opacity:0;transition:opacity .12s;white-space:nowrap;transform:translateY(-2px);";'
      + 'document.body.appendChild(tip);'
      + 'function move(e){var x=e.clientX+14,y=e.clientY-12;if(x>innerWidth-180)x=e.clientX-tip.offsetWidth-14;tip.style.left=x+"px";tip.style.top=y+"px";}'
      + 'document.addEventListener("mouseover",function(e){var t=e.target&&e.target.getAttribute&&e.target.getAttribute("data-tip");if(t){tip.textContent=t;tip.style.opacity="1";move(e);}});'
      + 'document.addEventListener("mousemove",function(e){if(tip.style.opacity==="1"){var t=e.target&&e.target.getAttribute&&e.target.getAttribute("data-tip");if(t)move(e);else tip.style.opacity="0";}});'
      + 'document.addEventListener("mouseout",function(e){if(e.target&&e.target.getAttribute&&e.target.getAttribute("data-tip"))tip.style.opacity="0";});'
      + '})();</script>\n'
      : '')

    +'</body>\n</html>';
}

function downloadReport(rep, clientName, history) {
  var html = generateReportHTML(rep, clientName, history);
  var blob = new Blob([html], {type:'text/html;charset=utf-8'});
  var url  = URL.createObjectURL(blob);
  var a    = document.createElement('a');
  var safe = clientName.replace(/[^a-z0-9]/gi,'_').toLowerCase();
  var m    = rep.monthYear.replace('-','_');
  var t    = rep.type==='social'?'social_media':'performance_marketing';
  a.href     = url;
  a.download = safe+'_'+t+'_'+m+'.html';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


// ── ReportDetail — proper component so useState obeys Rules of Hooks ────────
function ReportDetail({ rep, clientReports, clients, onBack, onDelete, onSave, getClientName, getClientAdType }) {
  const [editData, setEditData] = useState({ ...rep.data });
  const [pubBusy, setPubBusy]   = useState(false);
  const [shareUrl, setShareUrl] = useState(rep.data.shareUrl || '');
  const [copied, setCopied]     = useState(false);
  const [pubErr, setPubErr]     = useState('');

  // Publish an interactive, self-contained copy to the public bucket and
  // return a shareable URL. Re-publishing overwrites the same file (stable link).
  async function publishReport() {
    setPubBusy(true); setPubErr('');
    try {
      const shareId = editData.shareId || ('r' + String(rep.id).replace(/\D/g,'').slice(-8) + Math.random().toString(36).slice(2,8));
      const repForPub = { ...rep, data: editData };
      const html = generateReportHTML(repForPub, getClientName(rep.clientId), [repForPub, ...clientReports], { interactive: true });
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const path = shareId + '.html';
      const { error } = await supabase.storage.from('published-reports').upload(path, blob, { contentType: 'text/html', upsert: true });
      if (error) throw error;
      // Link points at the app's own /r/<id> route (which fetches + renders the
      // stored HTML) — Storage serves HTML as text/plain, so a raw link won't render.
      const url = window.location.origin + '/r/' + shareId;
      setShareUrl(url);
      onSave({ ...editData, shareId, shareUrl: url, publishedAt: new Date().toISOString() });
    } catch (e) {
      setPubErr((e && e.message) || 'Publish failed — please try again.');
    } finally { setPubBusy(false); }
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <Btn size="sm" variant="ghost" onClick={onBack}><ChevronLeft size={14}/>All reports</Btn>
        <div style={{flex:1}}/>
        <Btn size="sm" variant="ghost" onClick={()=>downloadReport(rep, getClientName(rep.clientId), [rep,...clientReports])}><Download size={12}/>Download HTML</Btn>
        <Btn size="sm" variant="ghost" onClick={()=>{ const w=window.open('','_blank'); w.document.write(generateReportHTML(rep,getClientName(rep.clientId),[rep,...clientReports])); w.document.close(); setTimeout(()=>w.print(),400); }}><Download size={12}/>Print / Save PDF</Btn>
        <Btn size="sm" variant="secondary" disabled={pubBusy} onClick={publishReport}><Share2 size={12}/>{pubBusy?'Publishing…':(shareUrl?'Re-publish':'Publish & Share')}</Btn>
        <Btn size="sm" variant="danger" onClick={onDelete}><Trash2 size={12}/>Delete</Btn>
        <Btn size="sm" variant="primary" onClick={()=>onSave(editData)}>Save changes</Btn>
      </div>

      {pubErr && <div style={{background:'var(--red-bg)',border:'1px solid rgba(192,128,121,0.30)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:13,color:'var(--red)'}}>{pubErr}</div>}
      {shareUrl && (
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'11px 14px'}}>
          <Share2 size={15} color="var(--accent2)"/>
          <span style={{fontSize:12,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text2)',textTransform:'uppercase',letterSpacing:'0.14em'}}>Public link</span>
          <a href={shareUrl} target="_blank" rel="noreferrer" style={{flex:1,minWidth:160,fontSize:12.5,color:'var(--accent2)',fontWeight:600,wordBreak:'break-all'}}>{shareUrl}</a>
          <Btn size="sm" variant="default" onClick={()=>{navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(()=>setCopied(false),2000);}}>{copied?<Check size={12}/>:<Copy size={12}/>}{copied?'Copied':'Copy'}</Btn>
          <Btn size="sm" variant="ghost" onClick={()=>window.open(shareUrl,'_blank')}><ExternalLink size={12}/>Open</Btn>
        </div>
      )}
      <Card>
        <div style={{padding:'1rem 1.25rem',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div>
            <div style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:16,color:'var(--text)'}}>{getClientName(rep.clientId)}</div>
            <div style={{fontSize:12,color:'var(--text3)',marginTop:2}}>
              {monthLabel(rep.monthYear)} · {rep.type==='social'?'Social Media Report':'Performance Marketing Report'}
            </div>
          </div>
          <Badge label={rep.type==='social'?'Social Media':'Performance'} color={rep.type==='social'?'blue':'purple'}/>
        </div>
        <div style={{padding:'1.25rem'}}>
          {rep.type==='performance'
            ? <PerfReportForm data={editData} setData={setEditData} adType={getClientAdType(clients,rep.clientId)}/>
            : <SocialReportForm data={editData} setData={setEditData}/>
          }
        </div>
      </Card>
      {clientReports.length > 0 && (
        <Card>
          <div style={{padding:'1rem 1.25rem'}}>
            {rep.type==='performance'
              ? <PerfComparison reports={[rep,...clientReports]}/>
              : <SocialComparison reports={[rep,...clientReports]}/>
            }
          </div>
        </Card>
      )}
    </div>
  );
}
